import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { addToast } from '@components';
import config from '@base/appSettings.json';
import './styles.css';

interface NewsletterPopupProps {
    show: boolean;
    onHide: () => void;
    userData?: {
        email?: string;
        firstName?: string;
        lastName?: string;
    };
}

const NewsletterPopup: React.FC<NewsletterPopupProps> = ({ show, onHide, userData }) => {
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async () => {
        if (!userData?.email) {
            addToast({
                message: 'Email address is required to subscribe to the newsletter',
                variant: 'error',
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${config.apiUrl}/Account/submit-newsletter-form`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userData.email
                }),
            });

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                console.error('Error parsing response:', jsonError);
                throw new Error('Invalid response from server');
            }

            if (!response.ok) {
                throw new Error(data?.message || `Server error: ${response.status}`);
            }

            addToast({
                message: data?.message || 'Successfully subscribed to newsletter!',
                variant: 'success',
            });
            onHide();
        } catch (error) {
            console.error('Newsletter subscription error:', error);
            addToast({
                message: error instanceof Error ? error.message : 'Failed to subscribe to newsletter',
                variant: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered className="newsletter-popup">
            <Modal.Header closeButton className="newsletter-popup-header">
                <Modal.Title id="newsletter-modal-title" className="newsletter-popup-title">
                    Follow the Onto News!
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="newsletter-popup-body">
                <p className="mb-4 newsletter-popup-text">
                    Receive free video minutes and be eligible for amazing gifts by joining our newsletter!
                </p>
                <div className="d-flex flex-column gap-3">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="marketingConsent"
                            checked={marketingConsent}
                            onChange={(e) => setMarketingConsent(e.target.checked)}
                            disabled={isLoading}
                        />
                        <label className="form-check-label" htmlFor="marketingConsent">
                            I agree to receive marketing and promotional communications from Ontoworks.
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="privacyConsent"
                            checked={privacyConsent}
                            onChange={(e) => setPrivacyConsent(e.target.checked)}
                            disabled={isLoading}
                        />
                        <label className="form-check-label" htmlFor="privacyConsent">
                            I agree that Ontoworks may store and process my personal data in accordance with its Privacy Policy.*
                        </label>
                    </div>
                    <p className="mt-3 small text-muted">
                        You can unsubscribe at any time. For details on how to unsubscribe, our privacy practices, and our commitment to protecting your data, please review our Privacy Policy.
                    </p>
                    <button
                        className="btn btn-primary mt-2"
                        onClick={handleSubmit}
                        disabled={!marketingConsent || !privacyConsent || isLoading}
                        style={{
                            backgroundColor: '#d41117',
                            borderColor: '#d41117',
                            color: 'white',
                            padding: '0.5rem 2rem',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            opacity: isLoading ? 0.7 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = '#b30f14';
                                e.currentTarget.style.borderColor = '#b30f14';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = '#d41117';
                                e.currentTarget.style.borderColor = '#d41117';
                            }
                        }}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Subscribing...
                            </>
                        ) : (
                            'Sign Up'
                        )}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// Define window.hbspt for TypeScript
declare global {
    interface Window {
        hbspt: any;
        resetNewsletterPopup: () => void;
    }
}

export default NewsletterPopup;
